import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  MenuItem,
  Chip,
  IconButton,
  Stack,
  Autocomplete,
  Grid,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadIcon from '@mui/icons-material/Upload';
import {
  OrganizationInsertDto,
  OrganizationSummaryDto,
  BatchOperationResultDto,
} from '../../api/types/dtos/organization.dto';
import { organizationService } from '../../api/services/organization.service';
import { useIdTypes } from '../../api/hooks/user.hooks';
import UploadReport from './UploadReport';

const CUSTOM_ID_TYPE = '__custom__';

const GENDER_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'O', label: 'Otro' },
];

interface CropRow {
  name: string;
  area: string;
}

interface ManualFarmFormProps {
  parentOrganizationId: string;
}

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const ManualFarmForm: React.FC<ManualFarmFormProps> = ({ parentOrganizationId }) => {
  const { data: idTypesData } = useIdTypes();
  const idTypeOptions = React.useMemo(() => {
    const types = (idTypesData?.data ?? []).filter((t) => t.isActive);
    return types.map((t) => ({ value: t.name, label: t.description ? `${t.name} - ${t.description}` : t.name }));
  }, [idTypesData]);

  const [formData, setFormData] = useState<Record<string, any>>({
    name: '',
    idTypeId: '',
    idNumber: '',
    logo: '',
    area: '',
    ownerName: '',
    ownerLastName: '',
    ownerMiddleName: '',
    ownerNickName: '',
    ownerIdTypeId: '',
    ownerIdNumber: '',
    ownerGender: '',
    ownerBirthdate: '',
    ownerEmail: '',
    ownerAddress: '',
    countryId: 'Colombia',
    regionId: '',
    cityId: '',
    corregimientoId: '',
    veredaId: '',
    latitude: '',
    longitude: '',
    elevation: '',
    geometry: '',
    malesFullTimeWorkers: '',
    malesTemporalWorkers: '',
    femaleFullTimeWorkers: '',
    femaleTemporalWorkers: '',
    isActive: true,
  });
  const [phones, setPhones] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [cropRows, setCropRows] = useState<CropRow[]>([{ name: '', area: '' }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<'form' | 'result'>('form');
  const [collision, setCollision] = useState<OrganizationSummaryDto | null>(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BatchOperationResultDto | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateField = useCallback((key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  useEffect(() => {
    const idNumber = formData.idNumber?.trim();
    if (!idNumber || !parentOrganizationId) return;
    setCollision(null);
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const check = await organizationService.checkExistingOrganizations(parentOrganizationId, {
          idNumbers: [idNumber],
        });
        const existing = check.existingOrgs?.[idNumber];
        if (existing) setCollision(existing);
      } catch {
        // Silent fail - will be retried on submit
      } finally {
        setChecking(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.idNumber, parentOrganizationId]);

  useEffect(() => {
    if (collision) {
      setFormData((prev) => ({ ...prev, isActive: collision.isActive }));
    }
  }, [collision]);

  const parseNumber = (val: string): number | null => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  };

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};
    if (!formData.idNumber?.trim()) e.idNumber = 'El código de finca es obligatorio';
    if (!collision) {
      if (!formData.name?.trim()) e.name = 'El nombre de la finca es obligatorio';
      if (!formData.idTypeId) e.idTypeId = 'Selecciona un tipo de ID';
      if (!formData.ownerName?.trim()) e.ownerName = 'El nombre del propietario es obligatorio';
      if (!formData.ownerIdTypeId) e.ownerIdTypeId = 'Selecciona un tipo de ID';
      if (!formData.ownerIdNumber?.trim()) e.ownerIdNumber = 'El documento del propietario es obligatorio';
      if (!formData.countryId?.trim()) e.countryId = 'El país es obligatorio';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData, collision]);

  const buildDto = useCallback((): OrganizationInsertDto => {
    const cropMap: Record<string, number> = {};
    for (const row of cropRows) {
      const name = row.name.trim();
      if (name) {
        cropMap[name] = parseNumber(row.area) ?? 0;
      }
    }
    const ownerBirthdate = formData.ownerBirthdate
      ? new Date(formData.ownerBirthdate).toISOString()
      : null;

    const dto: Record<string, unknown> = {
      idNumber: formData.idNumber.trim(),
      isActive: formData.isActive,
      isMain: false,
      isFarm: true,
    };
    if (formData.name?.trim()) dto.name = formData.name.trim();
    if (formData.idTypeId) dto.idTypeId = formData.idTypeId;
    if (formData.ownerName?.trim()) dto.ownerName = formData.ownerName.trim();
    if (formData.ownerIdTypeId) dto.ownerIdTypeId = formData.ownerIdTypeId;
    if (formData.ownerIdNumber?.trim()) dto.ownerIdNumber = formData.ownerIdNumber.trim();
    if (formData.countryId?.trim()) dto.countryId = formData.countryId.trim();
    if (formData.logo?.trim()) dto.logo = formData.logo.trim();
    if (formData.ownerLastName?.trim()) dto.ownerLastName = formData.ownerLastName.trim();
    if (formData.ownerMiddleName?.trim()) dto.ownerMiddleName = formData.ownerMiddleName.trim();
    if (formData.ownerNickName?.trim()) dto.ownerNickName = formData.ownerNickName.trim();
    if (formData.ownerGender) dto.ownerGender = formData.ownerGender;
    if (ownerBirthdate) dto.ownerBirthdate = ownerBirthdate;
    if (formData.ownerEmail?.trim()) dto.ownerEmail = formData.ownerEmail.trim();
    if (formData.ownerAddress?.trim()) dto.ownerAddress = formData.ownerAddress.trim();
    if (phones.length > 0) dto.ownerPhoneNumbers = phones.map((p) => p.trim()).filter(Boolean);
    if (formData.regionId?.trim()) dto.regionId = formData.regionId.trim();
    if (formData.cityId?.trim()) dto.cityId = formData.cityId.trim();
    if (formData.corregimientoId?.trim()) dto.corregimientoId = formData.corregimientoId.trim();
    if (formData.veredaId?.trim()) dto.veredaId = formData.veredaId.trim();
    if (parseNumber(formData.latitude) !== null) dto.latitude = parseNumber(formData.latitude);
    if (parseNumber(formData.longitude) !== null) dto.longitude = parseNumber(formData.longitude);
    if (parseNumber(formData.elevation) !== null) dto.elevation = parseNumber(formData.elevation);
    if (formData.geometry?.trim()) dto.geometry = formData.geometry.trim();
    if (parseNumber(formData.area) !== null) dto.area = parseNumber(formData.area);
    if (parseNumber(formData.malesFullTimeWorkers) !== null) dto.malesFullTimeWorkers = parseNumber(formData.malesFullTimeWorkers);
    if (parseNumber(formData.malesTemporalWorkers) !== null) dto.malesTemporalWorkers = parseNumber(formData.malesTemporalWorkers);
    if (parseNumber(formData.femaleFullTimeWorkers) !== null) dto.femaleFullTimeWorkers = parseNumber(formData.femaleFullTimeWorkers);
    if (parseNumber(formData.femaleTemporalWorkers) !== null) dto.femaleTemporalWorkers = parseNumber(formData.femaleTemporalWorkers);
    if (Object.keys(cropMap).length > 0) dto.crops = cropMap;
    if (groups.length > 0) dto.groupIds = groups.map((g) => g.trim()).filter(Boolean);
    return dto as unknown as OrganizationInsertDto;
  }, [formData, phones, groups, cropRows]);

  const doSubmit = useCallback(
    async (updateIfExists: boolean) => {
      setSubmitting(true);
      setSubmitError(null);
      setCollision(null);
      try {
        const dto = buildDto();
        const res = await organizationService.batchUpsertOrganizations(parentOrganizationId, {
          organizations: [dto],
          updateIfExists,
        });
        setResult(res);
        setPhase('result');
      } catch (err: any) {
        setSubmitError(err?.message || 'Ocurrió un error al crear la finca.');
      } finally {
        setSubmitting(false);
      }
    },
    [buildDto, parentOrganizationId],
  );

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    await doSubmit(!!collision);
  }, [collision, doSubmit, validate]);

  const handleReset = useCallback(() => {
    setFormData({
      name: '', idTypeId: '', idNumber: '', logo: '', area: '',
      ownerName: '', ownerLastName: '', ownerMiddleName: '', ownerNickName: '',
      ownerIdTypeId: '', ownerIdNumber: '', ownerGender: '', ownerBirthdate: '',
      ownerEmail: '', ownerAddress: '',
      countryId: 'Colombia', regionId: '', cityId: '', corregimientoId: '', veredaId: '',
      latitude: '', longitude: '', elevation: '', geometry: '',
      malesFullTimeWorkers: '', malesTemporalWorkers: '', femaleFullTimeWorkers: '', femaleTemporalWorkers: '',
      isActive: true,
    });
    setPhones([]);
    setGroups([]);
    setCropRows([{ name: '', area: '' }]);
    setErrors({});
    setCollision(null);
    setSubmitError(null);
    setResult(null);
    setPhase('form');
  }, []);

  const addCropRow = () => setCropRows((prev) => [...prev, { name: '', area: '' }]);
  const updateCropRow = (idx: number, field: keyof CropRow, value: string) =>
    setCropRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const removeCropRow = (idx: number) =>
    setCropRows((prev) => prev.filter((_, i) => i !== idx));

  const isBusy = checking || submitting;

  if (phase === 'result' && result) {
    return (
      <Box>
        <UploadReport
          reportData={result.created}
          updatedData={result.updated}
          skippedData={result.skipped}
          failedData={result.failed}
          mode="upsert"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleReset}>
            Crear otra finca
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
        Crear finca manualmente
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Completa los campos para registrar una finca. Los campos marcados con * son obligatorios.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {collision && collision.isActive && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          <AlertTitle>Esta finca ya existe</AlertTitle>
          Se encontró una finca con el código <strong>{collision.idNumber}</strong>:{' '}
          <strong>{collision.name}</strong>. Los campos que llenes actualizarán la finca existente;
          los que dejes en blanco se mantendrán sin cambios.
        </Alert>
      )}
      {collision && !collision.isActive && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          <AlertTitle>Esta finca existe pero está eliminada (inactiva)</AlertTitle>
          Se encontró una finca con el código <strong>{collision.idNumber}</strong>:{' '}
          <strong>{collision.name}</strong>. Para restaurarla, activa el interruptor &quot;Activa&quot; abajo.
          Mientras permanezca inactiva no será visible en los listados.
        </Alert>
      )}

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Sección: Información de la Finca */}
        <Box>
          <SectionHeader title="Información de la Finca" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre de la Finca *"
                fullWidth
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Tipo de ID *"
                fullWidth
                value={idTypeOptions.some((o) => o.value === formData.idTypeId) ? formData.idTypeId : CUSTOM_ID_TYPE}
                onChange={(e) => updateField('idTypeId', e.target.value === CUSTOM_ID_TYPE ? '' : e.target.value)}
                error={!!errors.idTypeId}
                helperText={errors.idTypeId}
                disabled={isBusy}
              >
                <MenuItem value="" disabled><em>Selecciona...</em></MenuItem>
                {idTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
                <MenuItem value={CUSTOM_ID_TYPE}><em>Personalizado...</em></MenuItem>
              </TextField>
              {formData.idTypeId !== '' && !idTypeOptions.some((o) => o.value === formData.idTypeId) && (
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  placeholder="Escribe el tipo de ID"
                  value={formData.idTypeId}
                  onChange={(e) => updateField('idTypeId', e.target.value)}
                  disabled={isBusy}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Código de Finca *"
                fullWidth
                value={formData.idNumber}
                onChange={(e) => updateField('idNumber', e.target.value)}
                error={!!errors.idNumber}
                helperText={errors.idNumber}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL del Logo"
                fullWidth
                value={formData.logo}
                onChange={(e) => updateField('logo', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Área Total (hectáreas)"
                type="number"
                fullWidth
                value={formData.area}
                onChange={(e) => updateField('area', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Sección: Propietario */}
        <Box>
          <SectionHeader title="Propietario" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Primer Nombre *"
                fullWidth
                value={formData.ownerName}
                onChange={(e) => updateField('ownerName', e.target.value)}
                error={!!errors.ownerName}
                helperText={errors.ownerName}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Segundo Nombre"
                fullWidth
                value={formData.ownerMiddleName}
                onChange={(e) => updateField('ownerMiddleName', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Apellido"
                fullWidth
                value={formData.ownerLastName}
                onChange={(e) => updateField('ownerLastName', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Apodo"
                fullWidth
                value={formData.ownerNickName}
                onChange={(e) => updateField('ownerNickName', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Tipo de ID del Propietario *"
                fullWidth
                value={idTypeOptions.some((o) => o.value === formData.ownerIdTypeId) ? formData.ownerIdTypeId : CUSTOM_ID_TYPE}
                onChange={(e) => updateField('ownerIdTypeId', e.target.value === CUSTOM_ID_TYPE ? '' : e.target.value)}
                error={!!errors.ownerIdTypeId}
                helperText={errors.ownerIdTypeId}
                disabled={isBusy}
              >
                <MenuItem value="" disabled><em>Selecciona...</em></MenuItem>
                {idTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
                <MenuItem value={CUSTOM_ID_TYPE}><em>Personalizado...</em></MenuItem>
              </TextField>
              {formData.ownerIdTypeId !== '' && !idTypeOptions.some((o) => o.value === formData.ownerIdTypeId) && (
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  placeholder="Escribe el tipo de ID"
                  value={formData.ownerIdTypeId}
                  onChange={(e) => updateField('ownerIdTypeId', e.target.value)}
                  disabled={isBusy}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Documento del Propietario *"
                fullWidth
                value={formData.ownerIdNumber}
                onChange={(e) => updateField('ownerIdNumber', e.target.value)}
                error={!!errors.ownerIdNumber}
                helperText={errors.ownerIdNumber}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Género"
                fullWidth
                value={formData.ownerGender}
                onChange={(e) => updateField('ownerGender', e.target.value)}
                disabled={isBusy}
              >
                {GENDER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha de Nacimiento"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.ownerBirthdate}
                onChange={(e) => updateField('ownerBirthdate', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Correo"
                fullWidth
                value={formData.ownerEmail}
                onChange={(e) => updateField('ownerEmail', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Dirección"
                fullWidth
                value={formData.ownerAddress}
                onChange={(e) => updateField('ownerAddress', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={phones}
                onChange={(_, val) => setPhones(val as string[])}
                disabled={isBusy}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} size="small" label={option} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Teléfonos del Propietario"
                    placeholder="Escribe un número y presiona Enter"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Sección: Ubicación */}
        <Box>
          <SectionHeader title="Ubicación" subtitle="Puedes ingresar nombres (ej. ciudad, región); se crearán si no existen." />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="País *"
                fullWidth
                value={formData.countryId}
                onChange={(e) => updateField('countryId', e.target.value)}
                error={!!errors.countryId}
                helperText={errors.countryId}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Región / Departamento"
                fullWidth
                value={formData.regionId}
                onChange={(e) => updateField('regionId', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ciudad"
                fullWidth
                value={formData.cityId}
                onChange={(e) => updateField('cityId', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Corregimiento"
                fullWidth
                value={formData.corregimientoId}
                onChange={(e) => updateField('corregimientoId', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Vereda"
                fullWidth
                value={formData.veredaId}
                onChange={(e) => updateField('veredaId', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Latitud"
                type="number"
                fullWidth
                value={formData.latitude}
                onChange={(e) => updateField('latitude', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Longitud"
                type="number"
                fullWidth
                value={formData.longitude}
                onChange={(e) => updateField('longitude', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Elevación (metros)"
                type="number"
                fullWidth
                value={formData.elevation}
                onChange={(e) => updateField('elevation', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Geometría (GeoJSON)"
                fullWidth
                value={formData.geometry}
                onChange={(e) => updateField('geometry', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Sección: Trabajadores */}
        <Box>
          <SectionHeader title="Trabajadores" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Hombres Tiempo Completo"
                type="number"
                fullWidth
                value={formData.malesFullTimeWorkers}
                onChange={(e) => updateField('malesFullTimeWorkers', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Hombres Temporales"
                type="number"
                fullWidth
                value={formData.malesTemporalWorkers}
                onChange={(e) => updateField('malesTemporalWorkers', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Mujeres Tiempo Completo"
                type="number"
                fullWidth
                value={formData.femaleFullTimeWorkers}
                onChange={(e) => updateField('femaleFullTimeWorkers', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Mujeres Temporales"
                type="number"
                fullWidth
                value={formData.femaleTemporalWorkers}
                onChange={(e) => updateField('femaleTemporalWorkers', e.target.value)}
                disabled={isBusy}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Sección: Cultivos */}
        <Box>
          <SectionHeader title="Cultivos" subtitle="Agrega cultivos y su área cultivada." />
          <Stack spacing={1}>
            {cropRows.map((row, idx) => (
              <Grid container spacing={1} key={idx} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nombre del cultivo"
                    fullWidth
                    size="small"
                    value={row.name}
                    onChange={(e) => updateCropRow(idx, 'name', e.target.value)}
                    disabled={isBusy}
                  />
                </Grid>
                <Grid item xs={8} sm={4}>
                  <TextField
                    label="Área"
                    fullWidth
                    size="small"
                    type="number"
                    value={row.area}
                    onChange={(e) => updateCropRow(idx, 'area', e.target.value)}
                    disabled={isBusy}
                    InputProps={{ endAdornment: <InputAdornment position="end">ha</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeCropRow(idx)}
                    disabled={isBusy || cropRows.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCropRow}
              disabled={isBusy}
              sx={{ alignSelf: 'flex-start' }}
            >
              Agregar cultivo
            </Button>
          </Stack>
        </Box>

        <Divider />

        {/* Sección: Grupos */}
        <Box>
          <SectionHeader title="Grupos" subtitle="Asocia la finca a uno o más grupos." />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={groups}
            onChange={(_, val) => setGroups(val as string[])}
            disabled={isBusy}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} size="small" label={option} {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Grupos"
                placeholder="Escribe un nombre de grupo y presiona Enter"
              />
            )}
          />
        </Box>

        <Divider />

        {/* Sección: Estado */}
        <Box>
          <SectionHeader title="Estado" />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                disabled={isBusy}
                color="success"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={formData.isActive ? 'Activa' : 'Inactiva'}
                  color={formData.isActive ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            }
          />
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
          <Button variant="outlined" onClick={handleReset} disabled={isBusy}>
            Limpiar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isBusy}
            startIcon={
              checking ? (
                <CircularProgress size={16} color="inherit" />
              ) : submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UploadIcon />
              )
            }
          >
            {checking ? 'Verificando...' : submitting ? 'Guardando...' : collision && !collision.isActive && formData.isActive ? 'Restaurar Finca' : collision ? 'Actualizar Finca' : 'Crear Finca'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ManualFarmForm;
